import { useQuery } from '@tanstack/react-query';
import { fetchOAuth } from './apps-oauth';

export interface AppManifestNavBadge {
  text: string;
  tone?: 'neutral' | 'accent' | 'warning';
}

export interface AppManifestNavEntry {
  label: string;
  path: string;
  icon?: string;
  required_scope?: string;
  required_plan_slug?: string;
  /** Contract v1.6: optional small label rendered next to the link (e.g. "New", "3"). */
  badge?: AppManifestNavBadge;
  /**
   * Contract v1.6: one level of nesting allowed. When present, the parent
   * renders as a dropdown trigger and the children render in the panel.
   * The parent's `path` is still used as a "default" link on tap of the
   * dropdown's primary area on mobile.
   */
  children?: AppManifestNavEntry[];
}

export type AppNavMode = 'append' | 'replace';

export interface AppManifestLauncher {
  /** v1.1 only defines "chat"; future kinds will go here. */
  type: 'chat';
  /** Tooltip / aria-label. Defaults to the app name. */
  label?: string;
  /** Lucide icon identifier. Defaults to "sparkles". */
  icon?: string;
}

export interface AppManifest {
  nav?: AppManifestNavEntry[];
  /**
   * Contract v1.6 §2: how this app's nav should compose with the host's
   * marketing nav while the user is inside the app's mount path.
   *   - 'append'  (default): app nav appears alongside the host nav
   *   - 'replace': app nav replaces the host nav; shell adds a "Back to site" link
   * A tenant admin can override this per-installation via
   * `AppMount.nav_mode_override`.
   */
  nav_mode?: AppNavMode;
  entitlements?: Record<string, Record<string, unknown>>;
  /**
   * Apps with a floating launcher surface (e.g. embedded chat drawer)
   * declare it here so Rumio renders the button at the host-page level
   * instead of inside the iframe — fixes the `position: fixed` + iframe
   * auto-resize collision (the button pins to iframe content, which can
   * grow past the visible viewport). See contract-v1.1 §5.5.
   */
  launcher?: AppManifestLauncher;
  [key: string]: unknown;
}

export interface AppMount {
  id: string;
  path_prefix: string;
  default_path: string;
  label: string | null;
  icon: string | null;
  sort_order: number;
  /** Contract v1.6 §2: tenant-set override; NULL means "use manifest's nav_mode". */
  nav_mode_override: AppNavMode | null;
  app: {
    id: string;
    client_id: string;
    name: string;
    icon_url: string | null;
    base_url: string;
    manifest: AppManifest;
  };
}

interface MountsResponse {
  mounts: AppMount[];
}

/**
 * Loads the active app mount points for the current tenant.
 * Used by App.tsx to render dynamic `<Route path="<prefix>/*">` for each
 * mounted app, and by Header.tsx (A5) to surface app nav entries in the shell.
 *
 * Mounts change rarely (admin action), so cache for 60s and don't refetch on
 * focus.
 */
export function useAppMounts() {
  return useQuery({
    queryKey: ['apps', 'mounts'],
    queryFn: async () => {
      const res = await fetchOAuth<MountsResponse>('/apps/mounts');
      return res.mounts;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Remove the `rumio_session` query param from an app-announced path. The
 * host mirrors `rumio:location` paths into its own address bar and
 * sessionStorage, so a token that rides along would leak into history and
 * referrers (and, before this guard, compounded into ever-growing
 * `?rumio_session=A?rumio_session=B…` URLs). Tolerates that malformed shape:
 * URLSearchParams parses it as a single key, which is dropped whole.
 */
export function stripSessionParam(path: string): string {
  const q = path.indexOf('?');
  if (q === -1 || !path.includes('rumio_session')) return path;
  const hashAt = path.indexOf('#', q);
  const search = hashAt === -1 ? path.slice(q + 1) : path.slice(q + 1, hashAt);
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const params = new URLSearchParams(search);
  params.delete('rumio_session');
  const rest = params.toString();
  return path.slice(0, q) + (rest ? `?${rest}` : '') + hash;
}
